import React from "react";
import Select from "../../../../common/Select.jsx";
import ToggleSwitch from "../../../../common/ToggleSwitch.jsx";
import CoordinateInput from "../component/CoordinateInput.jsx";
import MapboxHelpTooltip from "../component/MapboxHelpTooltip.jsx";
import AdvancedSetting from "../component/AdvancedSetting.jsx";

const PROJECTION_OPTIONS = [
    {value: 'globe', label: I18n.t('globe')},
    {value: 'equalEarth', label: I18n.t('equal_earth')},
    {value: 'naturalEarth', label: I18n.t('natural_earth')},
    {value: 'winkelTripel', label: I18n.t('winkel_tripel')},
    {value: 'albers', label: I18n.t('albers')},
    {value: 'lambertConformalConic', label: I18n.t('lambert_conformal_conic')},
    {value: 'equirectangular', label: I18n.t('equirectangular')},
    {value: 'mercator', label: I18n.t('mercator')}
]
  
const MAP_STYLE_OPTIONS = [
    {value: 'mapbox://styles/mapbox/streets-v12', label: I18n.t('mapbox_streets')},
    {value: 'mapbox://styles/mapbox/outdoors-v12', label: I18n.t('mapbox_outdoors')},
    {value: 'mapbox://styles/mapbox/light-v12', label: I18n.t('mapbox_light')},
    {value: 'mapbox://styles/mapbox/dark-v12', label: I18n.t('mapbox_dark')},
    {value: 'mapbox://styles/mapbox/satellite-v12', label: I18n.t('mapbox_satellite')},
    {value: 'mapbox://styles/mapbox/satellite-streets-v12', label: I18n.t('mapbox_satellite_streets')},
    {value: 'mapbox://styles/mapbox/navigation-day-v12', label: I18n.t('mapbox_navigation_day')},
    {value: 'mapbox://styles/mapbox/navigation-night-v12', label: I18n.t('mapbox_navigation_night')}
]


export const MAP_PROPS = {
    projection: {
        title: I18n.t('map_projection'),
        description: I18n.t('mapbox_map_projection'),
        type: "enum",
        options: PROJECTION_OPTIONS,
        default: 'mercator'
    },
    style: {
        type: "style", // object || string
        default: 'mapbox://styles/mapbox/standard',
        description: "The map's Mapbox style",
        title: I18n.t('style'),
        options: MAP_STYLE_OPTIONS
    },
    center: {
        type: "coordinate",
        default: [0,0],
        description: I18n.t('mapbox_map_center'),
        title: I18n.t('mapbox_initial_center')
    },
    bounds: {
        type: "double_coordinate",
        default: null,
        description: I18n.t('mapbox_map_bounds'),
        title: I18n.t('map_boundaries'),
        bounds_sw: {
            type: "coordinate",
            default: null,
            title: I18n.t('bounds_south_west'),
            description: I18n.t('bounds_south_west')
        },
        bounds_ne: {
            type: "coordinate",
            default: null,
            title: I18n.t('bounds_north_east'),
            description: I18n.t('bounds_north_east'),
        }
    },
    advancedSetting: {
        type: "json",
        description: I18n.t('mapbox_custom_settings'),
        title: I18n.t('advanced_settings')
    },
    zoom: {
        type: "numberSelect",
        default: 0,
        options: Array.from({ length: 23 }, (v,index) => ({value: index, label: index})), // 0-22
        title: I18n.t('zoom_level'),
        description: I18n.t('mapbox_map_zoom')
    },
    custom_style: {
        type: "boolean",
        default: false,
        title: I18n.t('custom_style'),
        description: I18n.t('mapbox_custom_style')
    },
    custom_style_url: {
        type: "string",
        default: "",
        title: I18n.t('custom_style_url'),
        description: I18n.t('mapbox_style_url')
    }
}

const MapSetting = ({data, index, updateField}) => {
    const currentMapField = data || {}
    return (
        <div>
            <label className="col-xs-6 col-md-6">
            {MAP_PROPS.projection.title}
            <MapboxHelpTooltip description={MAP_PROPS.projection.description} />
            <Select
                value={currentMapField.projection || MAP_PROPS.projection.default}
                options={MAP_PROPS.projection.options}
                onChange={(v) => updateField(`mapFields.${index}.projection`, v)}
            />
            </label>

            <label className="col-xs-6 col-md-6">
            {MAP_PROPS.style.title}
            <MapboxHelpTooltip description={MAP_PROPS.style.description} />
            <Select
                value={currentMapField.style || MAP_PROPS.style.default}
                options={MAP_PROPS.style.options}
                onChange={(v) => updateField(`mapFields.${index}.style`, v)}
            />
            </label>

            <label className="col-xs-12 col-md-12 d-flex">
                <label style={{alignItems: 'center'}}>{MAP_PROPS.custom_style.title}</label>
                <MapboxHelpTooltip description={MAP_PROPS.custom_style.description} className='mr-1' />
                <ToggleSwitch
                    isOn={currentMapField.custom_style}
                    defaultIsOn={MAP_PROPS.custom_style.default}
                    name='map-settings'
                    handleToggle={(e) => updateField(`mapFields.${index}.custom_style`, !currentMapField.custom_style)}
                />
            </label>
            {
            currentMapField.custom_style &&
            <label className="col-xs-12 col-md-12">
                {MAP_PROPS.custom_style_url.title}
                <MapboxHelpTooltip description={MAP_PROPS.custom_style_url.description} />
                <input
                    type="text"
                    className="form-control"
                    name="map_custom_style_url"
                    value={currentMapField.custom_style_url || MAP_PROPS.custom_style_url.default}
                    onChange={(e) => updateField(`mapFields.${index}.custom_style_url`, e.target.value)}
                />
            </label>
            }

            <CoordinateInput 
                base_name={`mapFields.${index}.initial_coord`} 
                value={currentMapField.initial_coord} 
                updateField={updateField}
                title={MAP_PROPS.center.title} 
                defaultCenter={MAP_PROPS.center.default}
                description={MAP_PROPS.center.description}
            />

            <label className="col-xs-12 col-md-12">
            {MAP_PROPS.zoom.title}
            <MapboxHelpTooltip description={MAP_PROPS.zoom.description} />
            <Select
                value={currentMapField.zoom || MAP_PROPS.zoom.default }
                options={MAP_PROPS.zoom.options}
                onChange={(v) => updateField(`mapFields.${index}.zoom`, v)}
            />
            </label>

            <label className="col-xs-12 col-md-12">
                {MAP_PROPS.bounds.title}
                <MapboxHelpTooltip description={MAP_PROPS.bounds.description} />
            </label>
            <label className="col-xs-12">
                <CoordinateInput
                    base_name={`mapFields.${index}.bounds_sw`}
                    value={currentMapField.bounds_sw}
                    updateField={updateField}
                    title={MAP_PROPS.bounds.bounds_sw.title}
                    description={MAP_PROPS.bounds.bounds_sw.description}
                />
            </label>
            <label className="col-xs-12">
                <CoordinateInput
                    base_name={`mapFields.${index}.bounds_ne`}
                    value={currentMapField.bounds_ne}
                    updateField={updateField}
                    title={MAP_PROPS.bounds.bounds_ne.title}
                    description={MAP_PROPS.bounds.bounds_ne.description}
                />
            </label>
            <AdvancedSetting 
                update={(val) => updateField(`mapFields.${index}.advancedSetting`, val)} 
                title={MAP_PROPS.advancedSetting.title}
                description={MAP_PROPS.advancedSetting.description}
                setting={currentMapField.advancedSetting}
            />
    </div>
    )
}

export default MapSetting